/**
 * Screen 2 — the money screen.
 *
 * A scan is 65–95 seconds, which is only tolerable if the work is visible. A
 * card per bottle appears as detection lands and its specialist pills tick over
 * as branches finish, so the wait reads as six researchers working rather than
 * as a spinner.
 */
import { useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import BottleTile from '../components/BottleTile'
import { STAGES, useScan } from '../hooks/useScan'
import { useSignedUrls } from '../hooks/useSignedUrls'

export default function ScanProgress() {
  const { runId } = useParams<{ runId: string }>()
  const navigate = useNavigate()
  const scan = useScan(runId)
  const resolved = useSignedUrls(scan.cards.map((card) => card.cropUri))

  // A shopping run is not quite finished when it says it is: the four critics
  // are asked over the ranking after the stream has closed. Leaving now would
  // land on a results screen whose critics section fills in behind a cached
  // query, so hold here until they arrive — or until they plainly are not
  // going to, because a failed panel is silent by design.
  const shopping = scan.result?.mode === 'shopping'
  const waitingForCritics =
    scan.status === 'complete' && shopping && !scan.shelfAnswers.length

  useEffect(() => {
    if (scan.status === 'complete') {
      // A beat on "Done" before the results slide in; jumping instantly reads
      // as a glitch.
      const timer = setTimeout(
        () => navigate(`/scan/${runId}/results`, { replace: true }),
        waitingForCritics ? 25_000 : 700,
      )
      return () => clearTimeout(timer)
    }
  }, [scan.status, waitingForCritics, runId, navigate])

  const totalStages = scan.cards.length * STAGES.length
  const doneStages = Object.values(scan.stages).reduce(
    (sum, stages) => sum + Object.values(stages).filter((s) => s === 'done').length,
    0,
  )
  const fraction = totalStages ? doneStages / totalStages : 0

  if (scan.status === 'failed') {
    return (
      <div className="space-y-4 pt-16 text-center">
        <p className="font-display text-2xl">That did not go well.</p>
        <p className="text-sm text-rose-300">{scan.error}</p>
        <button className="btn-ghost" onClick={() => navigate('/')}>
          Start again
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-6 pt-5">
      <section>
        <div className="flex items-baseline justify-between">
          <h2 className="font-display text-2xl">{scan.phase}</h2>
          {!scan.live && scan.status === 'running' && (
            <span className="text-[11px] text-stone-500">reconnecting…</span>
          )}
        </div>
        {scan.detected !== null && (
          <p className="mt-1 text-sm text-stone-400">
            {scan.detected} {scan.detected === 1 ? 'bottle' : 'bottles'} detected
            {scan.cards.length && scan.cards.length !== scan.detected
              ? `, ${scan.cards.length} legible`
              : ''}
            . {scan.sceneNotes}
          </p>
        )}
        <div className="mt-3 h-1 overflow-hidden rounded-full bg-white/8">
          <div
            className="h-full rounded-full bg-wine-600 transition-[width] duration-700"
            style={{ width: `${Math.max(4, fraction * 100)}%` }}
          />
        </div>
      </section>

      {!scan.cards.length && (
        <div className="grid gap-2">
          {[0, 1, 2].map((index) => (
            <div key={index} className="card h-24 animate-pulse opacity-40" />
          ))}
        </div>
      )}

      <div className="grid gap-2">
        {scan.cards.map((card) => (
          <BottleTile
            key={card.slotId}
            card={card}
            url={card.cropUrl || resolved[card.cropUri ?? '']}
            stages={scan.stages[card.slotId] ?? {}}
            match={scan.matched[card.slotId]}
            collapsedInto={scan.collapsed[card.slotId]}
          />
        ))}
      </div>

      {scan.verdict && (
        <section className="card p-4">
          <h3 className="label">The verdict</h3>
          <p className="caret mt-2 text-[15px] leading-relaxed whitespace-pre-wrap text-stone-200">
            {scan.verdict}
          </p>
        </section>
      )}

      {scan.narration.length > 0 && !scan.verdict && (
        <p className="text-center text-xs text-stone-500 italic">
          {scan.narration[scan.narration.length - 1]}
        </p>
      )}
    </div>
  )
}
